import {
  CommandsManager,
  ExtensionManager,
  ServicesManager,
  ServiceProvidersManager,
  HotkeysManager,
  UINotificationService,
  UIModalService,
  UIDialogService,
  UIViewportDialogService,
  MeasurementService,
  DisplaySetService,
  ToolbarService,
  ViewportGridService,
  HangingProtocolService,
  CineService,
  UserAuthenticationService,
  errorHandler,
  CustomizationService,
  PanelService,
  WorkflowStepsService,
  StudyPrefetcherService,
  MultiMonitorService,
  // utils,
} from '@ohif/core';

import loadModules, { loadModule as peerImport } from './pluginImports';
import { publicUrl } from './utils/publicUrl';

/**
 * @param {object|func} appConfigOrFunc - application configuration, or a function that returns application configuration
 * @param {object[]} defaultExtensions - array of extension objects
 */
async function appInit(appConfigOrFunc, defaultExtensions, defaultModes) {
  const commandsManagerConfig = {
    getAppState: () => {},
  };

  const commandsManager = new CommandsManager(commandsManagerConfig);
  const servicesManager = new ServicesManager(commandsManager);
  const serviceProvidersManager = new ServiceProvidersManager();
  const hotkeysManager = new HotkeysManager(commandsManager, servicesManager);

  const appConfig = {
    ...(typeof appConfigOrFunc === 'function'
      ? await appConfigOrFunc({ servicesManager, peerImport })
      : appConfigOrFunc),
  };
  // Default the peer import function
  appConfig.peerImport ||= peerImport;
  // Tracking disabled by default: no "Track measurements?" prompt and no
  // tracking UI. Drawing/measuring still works; measurements just aren't wrapped
  // in an SR tracking session.
  appConfig.measurementTrackingMode ||= 'none';
  // Enable per-tool mouse cursors so measurement tools show the pencil cursor
  // (and other tools get their meaningful cursors) instead of a plain pointer.
  appConfig.useCursors ??= true;
  appConfig.routerBasename ||= publicUrl;

  const extensionManager = new ExtensionManager({
    commandsManager,
    servicesManager,
    serviceProvidersManager,
    hotkeysManager,
    appConfig,
  });

  servicesManager.setExtensionManager(extensionManager);

  servicesManager.registerServices([
    [MultiMonitorService.REGISTRATION, appConfig.multimonitor],
    UINotificationService.REGISTRATION,
    UIModalService.REGISTRATION,
    UIDialogService.REGISTRATION,
    UIViewportDialogService.REGISTRATION,
    MeasurementService.REGISTRATION,
    DisplaySetService.REGISTRATION,
    [CustomizationService.REGISTRATION, appConfig.customizationService],
    ToolbarService.REGISTRATION,
    ViewportGridService.REGISTRATION,
    HangingProtocolService.REGISTRATION,
    CineService.REGISTRATION,
    UserAuthenticationService.REGISTRATION,
    PanelService.REGISTRATION,
    WorkflowStepsService.REGISTRATION,
    [StudyPrefetcherService.REGISTRATION, appConfig.studyPrefetcher],
  ]);

  // No red "stuck" popups around study load/open: transient load errors
  // (network hiccups, cancelled/aborted fetches, 4xx/5xx retrieve failures,
  // unsupported codecs) are logged instead of toasted — the viewer recovers or
  // degrades gracefully and the user shouldn't be alarmed. Any remaining error
  // toast is clamped to auto-dismiss so nothing ever sticks on screen.
  {
    const { uiNotificationService } = servicesManager.services;
    if (uiNotificationService?.show) {
      const TRANSIENT_LOAD_ERROR =
        /failed to fetch|network\s*error|cancell?ed|abort(ed)?|interrupted|time(d)?\s*out|status\s*[45]\d\d|no decoder|transfer syntax|couldn'?t retrieve|retrieve fail|load(ing)? failed|request failed|fetch fail/i;
      const originalShow = uiNotificationService.show.bind(uiNotificationService);
      uiNotificationService.show = options => {
        try {
          if (options?.type === 'error') {
            const text = `${options.title || ''} ${options.message || ''}`;
            // NEVER suppress critical save/store/submit failures — silently hiding a
            // "report failed to save" / SEG/SR store error is data loss on a
            // teleradiology PACS. Only pure image-LOAD noise is suppressed below.
            const isCritical =
              /report|store|stow|save|submit|sign|export|measurement|segmentation|annotation/i.test(
                text
              );
            if (!isCritical && TRANSIENT_LOAD_ERROR.test(text)) {
              console.warn('[Astra] suppressed transient load-error toast:', text.trim());
              return undefined;
            }
            if (!options.duration || options.duration <= 0) {
              options = { ...options, duration: 8000 };
            }
          }
        } catch (e) {
          /* never let the guard itself break notifications */
        }
        return originalShow(options);
      };
    }
  }

  errorHandler.getHTTPErrorHandler = () => {
    if (typeof appConfig.httpErrorHandler === 'function') {
      return appConfig.httpErrorHandler;
    }
  };

  /**
   * Example: [ext1, ext2, ext3]
   * Example2: [[ext1, config], ext2, [ext3, config]]
   */
  const loadedExtensions = await loadModules([...defaultExtensions, ...appConfig.extensions]);
  await extensionManager.registerExtensions(loadedExtensions, appConfig.dataSources);

  // TODO: We no longer use `utils.addServer`
  // TODO: We no longer init webWorkers at app level
  // TODO: We no longer init the user Manager

  if (!appConfig.modes) {
    throw new Error('No modes are defined! Check your app-config.js');
  }

  const loadedModes = await loadModules([...(appConfig.modes || []), ...defaultModes]);

  // This is the name for the loaded instance object
  appConfig.loadedModes = [];
  const modesById = new Set();
  for (let i = 0; i < loadedModes.length; i++) {
    let mode = loadedModes[i];
    if (!mode) {
      continue;
    }
    const { id } = mode;

    if (mode.modeFactory) {
      // If the appConfig contains configuration for this mode, use it.
      const modeConfiguration =
        appConfig.modesConfiguration && appConfig.modesConfiguration[id]
          ? appConfig.modesConfiguration[id]
          : {};

      mode = await mode.modeFactory({ modeConfiguration, loadModules });
    }

    if (modesById.has(id)) {
      continue;
    }
    // Prevent duplication
    modesById.add(id);
    if (!mode || typeof mode !== 'object') {
      continue;
    }
    appConfig.loadedModes.push(mode);
  }
  // Hack alert - don't touch the original modes definition,
  // but there are still dependencies on having the appConfig modes defined
  appConfig.modes = appConfig.loadedModes;

  return {
    appConfig,
    commandsManager,
    extensionManager,
    servicesManager,
    serviceProvidersManager,
    hotkeysManager,
  };
}

export default appInit;
